<template>
  <div class="members-management">
    <div class="page-breadcrumb">
      <span>會員管理</span> <i class="fas fa-chevron-right separator"></i> <span>會員列表</span>
    </div>

    <div class="filter-bar filter-bar-responsive">
      <input type="text" v-model="filters.displayName" placeholder="依顯示名稱搜尋..." class="form-input search-input" @keyup.enter="applyFiltersAndResetPage"/>
      <input type="text" v-model="filters.email" placeholder="依 Email 搜尋..." class="form-input search-input" @keyup.enter="applyFiltersAndResetPage"/>
      <select v-model="filters.role" class="form-input search-select" @change="applyFiltersAndResetPage">
        <option :value="null">所有角色</option>
        <option v-for="roleOption in roleOptions" :key="roleOption.value" :value="roleOption.value">
          {{ roleOption.label }}
        </option>
      </select>
      <select v-model="filters.enabled" class="form-input search-select boolean-select" @change="applyFiltersAndResetPage">
        <option :value="true">已啟用</option>
        <option :value="false">已停用</option>
      </select>
      <button class="filter-btn" @click="applyFiltersAndResetPage">
        <i class="fas fa-search"></i> 搜尋
      </button>
      <button class="clear-filter-btn" @click="clearFiltersAndFetch">
        <i class="fas fa-times"></i> 清除
      </button>
    </div>

    <div v-if="isLoading" class="loading-spinner">
      <p>載入會員資料中...</p>
    </div>

    <div v-if="!isLoading && members.length > 0" class="members-table-container">
      <table class="members-table">
        <thead>
        <tr>
          <th>頭像</th>
          <th>顯示名稱</th>
          <th>Email</th>
          <th>角色</th>
          <th>狀態</th>
          <th>操作</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="member in members" :key="member.id">
          <td>
            <img v-if="member.pictureUrl" :src="member.pictureUrl" :alt="member.displayName" class="member-avatar">
            <div v-else class="avatar-placeholder">
              <i class="fas fa-user"></i>
            </div>
          </td>
          <td>{{ member.displayName }}</td>
          <td>{{ member.email || '-' }}</td>
          <td>
            <span :class="['role-badge', getRoleClass(member.role)]">{{ getRoleDisplayName(member.role) }}</span>
          </td>
          <td>
              <span :class="['status-badge', member.enabled ? 'enabled' : 'disabled']">
                {{ member.enabled ? '已啟用' : '已停用' }}
              </span>
          </td>
          <td class="actions-cell">
            <button class="action-btn edit-btn" @click="openEditModal(member)">
              <i class="fas fa-user-shield"></i> 更新角色/狀態
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!isLoading && members.length === 0" class="no-data-message">
      目前沒有符合條件的會員資料。
    </div>

    <div class="pagination-controls" v-if="!isLoading && totalItems > 0">
      <div class="items-per-page-selector">
        <label for="itemsPerPageSelect">每頁顯示：</label>
        <select id="itemsPerPageSelect" v-model.number="itemsPerPage" @change="handleItemsPerPageChange" class="select-dropdown">
          <option v-for="option in perPageOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
        <span class="total-items-info"> (共 {{ totalItems }} 項)</span>
      </div>
      <div class="page-navigation">
        <button @click="goToPage(1)" :disabled="currentPage === 1" class="page-btn">
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
          <i class="fas fa-angle-left"></i>
        </button>
        <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>
        <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
          <i class="fas fa-angle-right"></i>
        </button>
        <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
          <i class="fas fa-angle-double-right"></i>
        </button>
      </div>
    </div>

    <div v-if="isModalVisible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content member-modal-content">
        <h4 class="modal-title">更新會員角色與狀態</h4>
        <form @submit.prevent="handleUpdateRoleAndStatus">
          <div class="form-group modal-member-info">
            <img v-if="currentMember.pictureUrl" :src="currentMember.pictureUrl" :alt="currentMember.displayName" class="member-avatar-modal">
            <div v-else class="avatar-placeholder-modal"><i class="fas fa-user"></i></div>
            <div>
              <strong>{{ currentMember.displayName }}</strong><br>
              <small>{{ currentMember.email || '無Email' }}</small>
            </div>
          </div>

          <div class="form-group">
            <label for="memberRole">角色：</label>
            <select id="memberRole" v-model="currentMember.role" required class="form-input" style="padding: 1px">
              <option v-for="roleOption in roleOptions" :key="roleOption.value" :value="roleOption.value">
                {{ roleOption.label }}
              </option>
            </select>
          </div>

          <div class="form-group toggle-form-group">
            <label for="memberEnabled" class="toggle-switch-label">帳號啟用狀態：</label>
            <label class="switch">
              <input type="checkbox" id="memberEnabled" v-model="currentMember.enabled">
              <span class="slider round"></span>
            </label>
          </div>

          <div class="modal-actions">
            <button type="button" @click="closeModal" class="modal-btn cancel-btn">取消</button>
            <button type="submit" class="modal-btn save-btn" :disabled="isSaving">
              <span v-if="isSaving"><i class="fas fa-spinner fa-spin"></i> 更新中...</span>
              <span v-else>更新</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
// Although not used for delete here, good to have if needed
import { getMemberPage, updateRoleAndStatus } from '@/api/auth.api.js'; // Assuming this is your member API file

const toast = useToast();

const members = ref([]);
const isLoading = ref(true);
const isSaving = ref(false); // For modal save button

// Modal state
const isModalVisible = ref(false);
const currentMember = ref({
  id: null,
  displayName: '',
  pictureUrl: '',
  email: '',
  role: 'USER', // Default role, assuming Role is an enum like 'USER', 'ADMIN'
  enabled: true,
});

// Pagination state
const currentPage = ref(1);
const itemsPerPage = ref(10);
const perPageOptions = ref([10, 20, 30, 100]);
const totalPages = ref(0);
const totalItems = ref(0);

// Filter state - matches MemberQueryRequest
const filters = reactive({
  displayName: '',
  email: '',
  role: null,      // null for all roles
  enabled: true,   // null for all statuses
});

// Role options for select dropdown and display
// Assuming your Role enum has values like 'USER', 'ADMIN'
const roleOptions = ref([
  { value: 'USER', label: '一般使用者' },
  { value: 'ADMIN', label: '管理員' },
  // Add other roles as needed
]);

const getRoleDisplayName = (roleValue) => {
  const role = roleOptions.value.find(r => r.value === roleValue);
  return role ? role.label : roleValue; // Fallback to raw value if not found
};
const getRoleClass = (roleValue) => {
  switch(roleValue) {
    case 'ADMIN': return 'role-admin';
    case 'USER': return 'role-user';
    default: return 'role-other';
  }
};


// API request function for members
const fetchMembers = async () => {
  isLoading.value = true;
  const queryRequest = {
    displayName: filters.displayName.trim() || undefined,
    email: filters.email.trim() || undefined,
    role: filters.role === null ? undefined : filters.role,
    enabled: filters.enabled === null ? undefined : filters.enabled,
  };
  Object.keys(queryRequest).forEach(key => {
    if (queryRequest[key] === undefined) {
      delete queryRequest[key];
    }
  });

  const pageable = {
    page: currentPage.value - 1, // Backend is 0-indexed
    size: itemsPerPage.value,
  };

  try {
    const response = await getMemberPage(queryRequest, pageable);
    members.value = response.content.map(m => ({
      ...m,
      enabled: !!m.enabled // Ensure boolean
    }));
    totalItems.value = response.totalElements;
    totalPages.value = response.totalPages;

    if (members.value.length === 0 && totalItems.value > 0 && currentPage.value > 1) {
      currentPage.value = totalPages.value > 0 ? totalPages.value : 1;
    } else if (totalPages.value > 0 && currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    } else if (totalPages.value === 0 && members.value.length === 0 ) {
      currentPage.value = 1;
    }
  } catch (error) {
    console.error("獲取會員失敗:", error);
    toast.error('獲取會員資料失敗，請稍後再試。');
    members.value = [];
    totalItems.value = 0;
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchMembers);

watch([currentPage, itemsPerPage], () => {
  fetchMembers();
});

const applyFiltersAndResetPage = () => {
  if (currentPage.value === 1) {
    fetchMembers();
  } else {
    currentPage.value = 1; // This will trigger the watcher
  }
};
const clearFiltersAndFetch = () => {
  filters.displayName = '';
  filters.email = '';
  filters.role = null;
  filters.enabled = null;
  applyFiltersAndResetPage();
};

// Modal methods
const openEditModal = (member) => {
  currentMember.value = { ...member }; // Copy member data to form
  isModalVisible.value = true;
};

const closeModal = () => {
  isModalVisible.value = false;
};

const handleUpdateRoleAndStatus = async () => {
  if (!currentMember.value.id) return;
  isSaving.value = true;

  const dataToUpdate = {
    role: currentMember.value.role,
    enabled: !!currentMember.value.enabled, // Ensure boolean
  };

  try {
    const success = await updateRoleAndStatus(currentMember.value.id, dataToUpdate);
    if (success) { // API returns true on 204
      toast.success(`會員 ${currentMember.value.displayName} 的角色/狀態已更新！`);
      closeModal();
      await fetchMembers(); // Refresh the list
    } else {
      // This case might not be hit if API throws error for non-204 status
      toast.error('更新失敗，後端未返回成功狀態。');
    }
  } catch (error) {
    console.error("更新會員角色/狀態失敗:", error);
    const errorMessage = error.response?.data?.message || error.message || '操作失敗，請稍後再試。';
    toast.error(`更新失敗：${errorMessage}`);
  } finally {
    isSaving.value = false;
  }
};
// Re-add pagination methods if they were removed from the snippet view
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    currentPage.value = page;
  }
};
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};
const handleItemsPerPageChange = () => {
  if (currentPage.value === 1) {
    fetchMembers();
  } else {
    currentPage.value = 1;
  }
};

</script>

<style scoped>
.members-management {
  padding: 20px;
  color: #c0c0c0;
}

.page-breadcrumb {
  font-size: 0.95em;
  color: #a0a0b0;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
}
.page-breadcrumb .separator { margin: 0 8px; font-size: 0.7em; }

/* Filter Bar Styles (similar to ProductsView, adjust as needed) */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 25px;
  align-items: center;
  background-color: rgba(30, 30, 40, 0.7);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.3);
}

.form-input {
  padding: 10px 12px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  height: 38px;
}
.form-input:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}

.form-input.search-input {
  flex: 1 1 180px; /* Adjusted flex-basis */
}
.form-input.search-select {
  flex: 1 1 140px; /* Adjusted flex-basis */
  min-width: 120px;
  padding-top: 0;
  padding-bottom: 0;
  height: 38px;
}
.form-input.search-select.boolean-select {
  flex: 0 1 130px;
  min-width: 120px;
}


.filter-btn, .clear-filter-btn {
  padding: 0 15px;
  height: 38px;
  line-height: 36px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-width: 90px;
  flex-shrink: 0;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.filter-btn i, .clear-filter-btn i { margin-right: 6px; }

.filter-btn {
  background: linear-gradient(145deg, #8be9fd, #50c8ea);
  color: #1a1a1a;
  border-color: #50c8ea;
}
.filter-btn:hover {
  background: linear-gradient(145deg, #a0eefe, #60d0f0);
  box-shadow: 0 2px 8px rgba(139, 233, 253, 0.2);
}

.clear-filter-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
  border-color: rgba(100,100,110,0.8);
}
.clear-filter-btn:hover {
  background-color: rgba(90, 90, 100, 0.8);
}

.loading-spinner, .no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

/* Table Styles */
.members-table-container {
  background-color: rgba(30, 30, 40, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 0; /* No padding for container, table will handle it */
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  overflow-x: auto;
  margin-bottom: 30px;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
  color: #c0c0c0;
}

.members-table th,
.members-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid rgba(160, 100, 220, 0.15);
  vertical-align: middle;
}

.members-table th {
  background-color: rgba(40, 40, 55, 0.8);
  font-weight: 600;
  color: #e0e0e0;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.members-table tr:last-child td {
  border-bottom: none;
}

.members-table tr:hover td {
  background-color: rgba(50, 50, 65, 0.7);
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(160, 100, 220, 0.5);
}
.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(80, 80, 90, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a0b0;
  font-size: 1.2em;
  border: 2px solid rgba(160, 100, 220, 0.3);
}
.line-user-id {
  font-size: 0.8em;
  color: #888e95;
  word-break: break-all;
}

.role-badge, .status-badge {
  padding: 4px 8px;
  border-radius: 12px; /* More pill-like */
  font-size: 0.8em;
  font-weight: 500;
  display: inline-block;
}
.role-admin { background-color: #ff8c69; color: #1a1a1a; }
.role-user { background-color: #8be9fd; color: #1a1a1a; }
.role-other { background-color: #6c757d; color: #ffffff; }

.status-badge.enabled { background-color: #50fa7b; color: #1a1a1a; }
.status-badge.disabled { background-color: #ff5555; color: #ffffff; }


.actions-cell {
  text-align: center; /* Center the button */
  white-space: nowrap;
}

.action-btn {
  background: none;
  border: 1px solid transparent;
  color: #adb5bd;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9em;
  margin-left: 8px;
}
.action-btn i { margin-right: 5px; }
.action-btn.edit-btn {
  border-color: rgba(139, 233, 253, 0.5);
  color: #8be9fd;
}
.action-btn.edit-btn:hover {
  background-color: rgba(139, 233, 253, 0.15);
  color: #a0eefe;
  border-color: rgba(139, 233, 253, 0.8);
}


/* Pagination Controls Styles (same as ProductsView) */
.pagination-controls { /* ... */ }
.items-per-page-selector { /* ... */ }
.select-dropdown { /* ... */ }
.total-items-info { /* ... */ }
.page-navigation { /* ... */ }
.page-btn { /* ... */ }
.page-info { /* ... */ }

/* Modal Styles (similar to ProductsView, adjust as needed) */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.75); display: flex;
  align-items: center; justify-content: center; z-index: 1000;
  overflow-y: auto; padding: 20px; box-sizing: border-box;
}
.modal-content { /* General modal content */
  background-color: rgba(30, 30, 40, 0.98); padding: 30px 40px;
  border-radius: 8px; border: 1px solid rgba(160, 100, 220, 0.6);
  box-shadow: 0 8px 30px rgba(0,0,0,0.6); width: 100%;
  max-width: 450px; /* Slightly narrower for this modal */
  color: #e0e0e0; margin: auto;
}
.modal-title {
  color: #ff8c69; margin-top: 0; margin-bottom: 25px;
  font-size: 1.5em; text-align: center; font-weight: 600;
}
.form-group { margin-bottom: 20px; }
.form-group label:not(.switch):not(.toggle-switch-label) {
  display: block; margin-bottom: 8px; font-size: 0.9em; color: #a0a0b0;
}

.modal-member-info {
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: rgba(20, 20, 30, 0.5);
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
}
.member-avatar-modal {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}
.avatar-placeholder-modal {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(80, 80, 90, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a0b0;
  font-size: 1.5em;
}
.modal-member-info strong { color: #e0e0e0; font-size: 1.05em; }
.modal-member-info small { color: #888e95; font-size: 0.85em; }


.form-group.toggle-form-group {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px; padding: 3px 0;
}
.toggle-switch-label {
  font-size: 0.9em; color: #a0a0b0; margin-right: 15px;
  line-height: 28px; white-space: nowrap;
}
.switch {
  position: relative; display: inline-block; width: 50px; height: 28px; flex-shrink: 0;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(80, 80, 90, 0.7); transition: .3s;
  border: 1px solid rgba(120, 120, 130, 0.5);
}
.slider:before {
  position: absolute; content: ""; height: 20px; width: 20px;
  left: 3px; bottom: 3px; background-color: #e0e0e0; transition: .3s;
}
#memberEnabled:checked + .slider { background-color: #50fa7b; border-color: #3ddc67; }
input:focus + .slider { box-shadow: 0 0 8px rgba(139, 233, 253, 0.4); }
#memberEnabled:checked:focus + .slider { box-shadow: 0 0 8px rgba(80, 250, 123, 0.6); }
input:checked + .slider:before { transform: translateX(22px); background-color: #1a1a1a; }
.slider.round { border-radius: 28px; }
.slider.round:before { border-radius: 50%; }


.modal-actions { display: flex; justify-content: flex-end; gap: 15px; margin-top: 30px;}
.modal-btn {
  padding: 10px 20px; border: none; border-radius: 5px;
  font-size: 0.95em; font-weight: 500; cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
}
.modal-btn.cancel-btn { background-color: rgba(80, 80, 90, 0.7); color: #c0c0c0; }
.modal-btn.cancel-btn:hover { background-color: rgba(90, 90, 100, 0.8); }
.modal-btn.save-btn { background: linear-gradient(145deg, #50fa7b, #3ddc67); color: #1a1a1a; }
.modal-btn.save-btn:hover:not(:disabled) { background: linear-gradient(145deg, #60fc8b, #4dfd77); transform: translateY(-1px); }
.modal-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.modal-btn.save-btn i { margin-right: 5px; }

/* RWD Styles (similar to ProductsView) */
@media (max-width: 768px) {
  .filter-bar { /* ... */ }
  .members-table th, .members-table td { padding: 8px 10px; font-size: 0.85em; }
  .member-avatar, .avatar-placeholder { width: 30px; height: 30px; font-size: 1em; border-width: 1px; }
  .line-user-id { display: none; } /* Hide on very small screens */
  .modal-content { max-width: 95%; padding: 20px; }
}
</style>